<script setup lang="ts">
import { reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { ChevronRight, Clock, FileText, Wrench } from 'lucide-vue-next'
import { logout, updatePassword } from '@ck/auth'
import { accountsApi } from '@ck/api-client'
import { CkAlert, CkButton, CkField } from '@ck/ui'
import BottomNav from '../components/BottomNav.vue'

const router = useRouter()
const account = useQuery({ queryKey: ['accounts', 'me'], queryFn: () => accountsApi.me() })
const shortcuts = [
  { label: 'Minhas ordens', icon: Wrench, route: { name: 'home' } },
  { label: 'Relatórios gerados', icon: FileText, route: { name: 'relatorios' } },
  { label: 'Histórico do dia', icon: Clock, route: { name: 'historico' } }
]

const form = reactive({ name: '', phone: '' })
const errors = ref<{ name?: string }>({})
const saved = ref(false)

watch(() => account.data.value, (data) => {
  if (!data) return
  form.name = data.name ?? ''
  form.phone = data.phone ?? ''
}, { immediate: true })

const mutation = useMutation({
  mutationFn: () => accountsApi.updateMe({ name: form.name.trim(), phone: form.phone.trim() || null }),
  onSuccess: () => { saved.value = true },
  onError: () => { saved.value = false }
})

function submit() {
  saved.value = false
  errors.value = {}
  if (form.name.trim().length < 3) errors.value.name = 'Nome deve ter ao menos 3 caracteres.'
  if (Object.keys(errors.value).length > 0) return
  mutation.mutate()
}
</script>

<template>
  <div class="app">
    <header><h1>Perfil</h1></header>
    <section class="card shortcuts">
      <button v-for="shortcut in shortcuts" :key="shortcut.label" type="button" class="shortcut" @click="router.push(shortcut.route)">
        <component :is="shortcut.icon" :size="19"/>
        <span>{{ shortcut.label }}</span>
        <ChevronRight :size="18" class="chevron"/>
      </button>
    </section>
    <section class="card">
      <CkAlert v-if="saved" tone="success">Dados atualizados.</CkAlert>
      <CkAlert v-else-if="mutation.isError.value" tone="error">Não foi possível salvar. Tente novamente.</CkAlert>
      <form @submit.prevent="submit">
        <CkField v-model="form.name" label="Nome completo" autocomplete="name" required :error="errors.name" :disabled="mutation.isPending.value" />
        <CkField label="E-mail" :model-value="account.data.value?.email ?? ''" type="email" disabled hint="O e-mail não pode ser alterado por aqui." />
        <CkField v-model="form.phone" label="Telefone" type="tel" autocomplete="tel" hint="Opcional" :disabled="mutation.isPending.value" />
        <CkButton type="submit" :disabled="mutation.isPending.value">{{ mutation.isPending.value ? 'Salvando…' : 'Salvar alterações' }}</CkButton>
      </form>
    </section>
    <section class="card">
      <h2>Segurança</h2>
      <div class="actions">
        <CkButton variant="secondary" @click="updatePassword">Alterar senha</CkButton>
        <CkButton variant="ghost" @click="logout">Sair</CkButton>
      </div>
    </section>
    <BottomNav/>
  </div>
</template>

<style scoped>
.app{max-width:620px;min-height:100vh;margin:auto;padding:calc(20px + env(safe-area-inset-top)) 18px calc(94px + env(safe-area-inset-bottom));background:var(--ck-cream-100)}
header{margin-bottom:20px}
header h1{margin:0;font-size:30px}
.card{padding:18px;margin-bottom:14px;border:1px solid var(--ck-cream-300);border-radius:var(--radius-lg);background:var(--ck-cream-50)}
.card h2{margin:0 0 14px;font-size:16px}
.actions{display:flex;gap:10px;flex-wrap:wrap}
.shortcuts{padding:6px 4px}
.shortcut{display:flex;align-items:center;gap:12px;width:100%;padding:12px 14px;border:0;border-radius:var(--radius-md);background:transparent;color:var(--ck-maroon-700);font:inherit;text-align:left;cursor:pointer}
.shortcut:hover,.shortcut:focus-visible{background:var(--ck-cream-200)}
.shortcut span{flex:1;color:var(--ck-text);font-size:15px}
.shortcut .chevron{color:var(--ck-text-faint)}
</style>
